<template>
  <section
    id="ways"
    class="bg-gray-100"
  >
    <div class="mx-auto flex max-w-7xl flex-col gap-12 px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
      <!-- Section header -->
      <div class="mx-auto flex w-full max-w-[800px] flex-col items-center gap-2 text-center">
        <span class="text-sm font-semibold uppercase tracking-wider text-primary-600">One editor, two ways to use it</span>
        <h2 class="text-pretty text-3xl font-semibold tracking-[-0.4px] text-gray-950 sm:text-4xl sm:tracking-[-0.72px] lg:text-balance">
          Embed it in your platform, or open the studio yourself.
        </h2>
        <p class="text-pretty text-lg text-gray-700 sm:text-xl sm:leading-[30px]">
          Same editor, same print-ready HTML. Platforms white-label it inside their product. Marketers use it as a self-service postcard studio and
          send through the provider they already have.
        </p>
      </div>

      <!-- Two cards -->
      <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <!-- Card 1: Platform embed -->
        <article class="flex flex-col overflow-hidden rounded-2xl border border-gray-300 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.1)]">
          <!-- Visual: browser chrome + platform mock -->
          <div class="border-b border-gray-300 bg-gradient-to-b from-primary-50 to-gray-100 p-6 pb-0">
            <div class="overflow-hidden rounded-t-xl border border-gray-300 bg-white shadow-[0px_4px_12px_-2px_rgba(16,24,40,0.12)]">
              <!-- Browser bar -->
              <div class="flex items-center gap-2 border-b border-gray-300 bg-gray-100 px-3 py-2">
                <span class="flex gap-1.5">
                  <i
                    class="block size-2 rounded-full bg-gray-400"
                    aria-hidden="true"
                  />
                  <i
                    class="block size-2 rounded-full bg-gray-400"
                    aria-hidden="true"
                  />
                  <i
                    class="block size-2 rounded-full bg-gray-400"
                    aria-hidden="true"
                  />
                </span>
                <span class="ml-2 flex-1 truncate rounded bg-white px-2 py-0.5 font-mono text-[10px] text-gray-500">
                  app.acmemail.com/campaigns/spring-listings/design
                </span>
              </div>
              <!-- Platform UI mock -->
              <div
                class="grid min-h-[180px]"
                style="grid-template-columns: 88px 1fr"
              >
                <!-- Sidebar -->
                <div class="flex flex-col gap-1 border-r border-gray-300 bg-white p-2">
                  <div class="mb-2 flex items-center gap-1.5">
                    <span
                      class="flex size-3.5 items-center justify-center rounded bg-[#0F766E]"
                      aria-hidden="true"
                    />
                    <span class="text-[10px] font-bold text-gray-950">Acme Mail</span>
                  </div>
                  <span class="rounded px-1.5 py-1 text-[10px] text-gray-600">Campaigns</span>
                  <span class="rounded bg-gray-200 px-1.5 py-1 text-[10px] font-semibold text-gray-950">Designs</span>
                  <span class="rounded px-1.5 py-1 text-[10px] text-gray-600">Audiences</span>
                  <span class="rounded px-1.5 py-1 text-[10px] text-gray-600">Reports</span>
                </div>
                <!-- Editor area -->
                <div class="flex flex-col">
                  <div class="flex items-center justify-between border-b border-gray-300 px-3 py-1.5">
                    <span class="text-[10px] font-semibold text-gray-950">Spring listings · Design</span>
                    <span class="rounded bg-[#0F766E] px-2 py-0.5 text-[9px] font-semibold text-white">Send to 1,240</span>
                  </div>
                  <div class="relative m-2 flex flex-1 overflow-hidden rounded border border-dashed border-primary-300 bg-primary-50">
                    <!-- Tools strip -->
                    <div class="flex flex-col items-center gap-1.5 border-r border-primary-200 p-1.5">
                      <span
                        v-for="n in 5"
                        :key="n"
                        class="block size-3 rounded bg-primary-200"
                        :class="n === 1 ? 'bg-primary-600' : ''"
                        aria-hidden="true"
                      />
                    </div>
                    <!-- Canvas -->
                    <div class="flex flex-1 items-center justify-center p-2">
                      <div
                        class="relative flex h-16 w-24 flex-col justify-end overflow-hidden rounded p-1.5 text-white shadow-md"
                        style="background: linear-gradient(160deg, #6941c6, #b692f6)"
                        aria-label="Postcard design preview"
                      >
                        <span class="text-[7px] font-bold leading-tight">Just listed</span>
                        <span class="text-[6px] opacity-80"
                          >Hi <em class="rounded bg-white/20 px-0.5 not-italic">{{ first_name }} </em></span
                        >
                      </div>
                    </div>
                    <!-- Properties panel -->
                    <div class="flex w-16 flex-col gap-1.5 border-l border-primary-200 bg-white p-1.5">
                      <span class="text-[8px] font-semibold uppercase text-gray-600">Text</span>
                      <span class="h-1.5 rounded bg-gray-200" />
                      <span class="h-1.5 w-3/4 rounded bg-gray-200" />
                      <span class="text-[8px] font-semibold uppercase text-gray-600">Fill</span>
                      <span class="size-3 rounded bg-primary-600" />
                    </div>
                    <!-- White-label badge -->
                    <span class="absolute right-1.5 top-1.5 rounded-full bg-primary-600 px-1.5 py-px text-[7px] font-semibold text-white">
                      Your brand · embedded
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Body -->
          <div class="flex flex-1 flex-col gap-5 p-6 sm:p-8">
            <div>
              <span class="inline-flex items-center rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
                For platforms
              </span>
            </div>
            <h3 class="text-2xl font-semibold text-gray-950">Inside your product</h3>
            <p class="text-base text-gray-700">
              Mount the editor on your domain with your logo and colours. Your users never leave your app, and you get the HTML back by webhook or
              API.
            </p>
            <ul class="flex flex-col gap-2">
              <li
                v-for="feat in platformFeatures"
                :key="feat"
                class="flex items-start gap-2.5 text-sm text-gray-800"
              >
                <svg
                  class="mt-0.5 size-4 shrink-0 text-primary-600"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12l5 5L20 7" />
                </svg>
                {{ feat }}
              </li>
            </ul>
            <div class="flex flex-wrap gap-3">
              <Link
                href="/developers"
                class="relative flex items-center gap-1.5 overflow-hidden rounded-lg bg-gradient-to-b from-primary-600 to-primary-700 px-4 py-2 text-sm font-semibold text-white shadow-[0px_1px_0px_0px_rgba(16,24,40,0.08)]"
              >
                <span
                  class="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0px_-2px_0px_0px_rgba(16,24,40,0.2)]"
                  aria-hidden="true"
                />
                See the embed docs
              </Link>
              <Link
                href="/pricing"
                class="flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-semibold text-primary-700 hover:bg-primary-50"
              >
                Platform plan →
              </Link>
            </div>
            <!-- Logos row -->
            <div class="mt-auto flex flex-wrap items-center gap-3 border-t border-gray-300 pt-4">
              <span class="text-xs font-semibold text-gray-600">Embedded by</span>
              <img
                :src="DmmLogo"
                alt="Direct Mail Manager"
                class="h-5 w-auto opacity-60"
              />
            </div>
          </div>
        </article>

        <!-- Card 2: Studio self-service -->
        <article class="flex flex-col overflow-hidden rounded-2xl border border-gray-300 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.1)]">
          <!-- Visual: browser chrome + studio grid mock -->
          <div class="border-b border-gray-300 bg-gradient-to-b from-gray-100 to-gray-200 p-6 pb-0">
            <div class="overflow-hidden rounded-t-xl border border-gray-300 bg-white shadow-[0px_4px_12px_-2px_rgba(16,24,40,0.12)]">
              <!-- Browser bar -->
              <div class="flex items-center gap-2 border-b border-gray-300 bg-gray-100 px-3 py-2">
                <span class="flex gap-1.5">
                  <i
                    class="block size-2 rounded-full bg-[#d0d5dd]"
                    aria-hidden="true"
                  />
                  <i
                    class="block size-2 rounded-full bg-[#d0d5dd]"
                    aria-hidden="true"
                  />
                  <i
                    class="block size-2 rounded-full bg-[#d0d5dd]"
                    aria-hidden="true"
                  />
                </span>
                <span class="ml-2 flex-1 truncate rounded bg-white px-2 py-0.5 font-mono text-[10px] text-gray-500">
                  studio.designertool.io/designs
                </span>
              </div>
              <!-- Studio grid mock -->
              <div class="flex min-h-[180px] flex-col">
                <div class="flex items-center justify-between border-b border-gray-300 px-3 py-1.5">
                  <span class="text-[10px] font-bold text-gray-950">Postcard Studio</span>
                  <span class="rounded bg-primary-600 px-2 py-0.5 text-[9px] font-semibold text-white">+ New postcard</span>
                </div>
                <div class="grid grid-cols-3 gap-2 p-3">
                  <div
                    v-for="card in studioCards"
                    :key="card.label"
                    class="flex flex-col gap-1"
                  >
                    <div
                      class="flex h-14 flex-col justify-end overflow-hidden rounded p-1.5 text-white"
                      :style="{ background: card.bg }"
                      aria-hidden="true"
                    >
                      <span class="text-[7px] font-bold leading-tight">{{ card.title }}</span>
                    </div>
                    <div class="flex items-center justify-between">
                      <span class="text-[9px] font-semibold text-gray-950">{{ card.label }}</span>
                      <span
                        class="rounded-full px-1 py-px text-[7px] font-semibold"
                        :class="card.statusClass"
                        >{{ card.status }}</span
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Body -->
          <div class="flex flex-1 flex-col gap-5 p-6 sm:p-8">
            <div>
              <span class="inline-flex items-center rounded-full bg-gray-200 px-3 py-1 text-xs font-semibold text-gray-800">
                For marketers and agencies
              </span>
            </div>
            <h3 class="text-2xl font-semibold text-gray-950">Postcard Studio, self-service</h3>
            <p class="text-base text-gray-700">
              Open the studio, pick a template, personalise from a CSV and send through Lob or PostGrid. No developer, no HTML, no plugin.
            </p>
            <ul class="flex flex-col gap-2">
              <li
                v-for="feat in studioFeatures"
                :key="feat"
                class="flex items-start gap-2.5 text-sm text-gray-800"
              >
                <svg
                  class="mt-0.5 size-4 shrink-0 text-primary-600"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12l5 5L20 7" />
                </svg>
                {{ feat }}
              </li>
            </ul>
            <div class="flex flex-wrap gap-3">
              <Link
                href="/signup"
                class="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-gradient-to-b from-white to-gray-50 px-4 py-2 text-sm font-semibold text-gray-800 shadow-[0px_1px_0px_0px_rgba(16,24,40,0.08)]"
              >
                Start free
              </Link>
              <Link
                href="/pricing"
                class="flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-semibold text-primary-700 hover:bg-primary-50"
              >
                Studio plan →
              </Link>
            </div>
            <!-- Logos row -->
            <div class="mt-auto flex flex-wrap items-center gap-3 border-t border-gray-300 pt-4">
              <span class="text-xs font-semibold text-gray-600">Sends through</span>
              <img
                :src="LobLogo"
                alt="Lob"
                class="h-5 w-auto opacity-60"
              />
              <img
                :src="PostgridLogo"
                alt="PostGrid"
                class="h-5 w-auto opacity-60"
              />
              <img
                :src="DmmLogo"
                alt="Direct Mail Manager"
                class="h-5 w-auto opacity-60"
              />
            </div>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { Link } from "@inertiajs/vue3";
import DmmLogo from "@/../images/integration-icons/dmm-logo.svg";
import LobLogo from "@/../images/integration-icons/lob-icon.svg";
import PostgridLogo from "@/../images/integration-icons/postgrid-icon.svg";

const platformFeatures = [
  "Script tag, iframe or React component",
  "Session tokens per end user, unlimited users",
  "Merge fields mapped to your send pipeline",
];

const studioFeatures = [
  "Templates sized for 4×6, 6×9, 6×11 and letters",
  "Recipient preview that catches overflow before print",
  "Export HTML or PDF, or send with one click",
];

const studioCards = [
  {
    title: "Just listed",
    label: "Maple St",
    status: "Sent · Lob",
    statusClass: "bg-success-50 text-success-600",
    bg: "linear-gradient(160deg, #6941c6, #b692f6)",
  },
  {
    title: "Check-up",
    label: "Recall Q3",
    status: "Exported",
    statusClass: "bg-primary-100 text-primary-700",
    bg: "linear-gradient(160deg, #0F766E, #5EEAD4)",
  },
  {
    title: "We miss you",
    label: "Win-back",
    status: "Draft",
    statusClass: "bg-gray-200 text-gray-800",
    bg: "linear-gradient(160deg, #C2410C, #FDBA74)",
  },
  {
    title: "You're invited",
    label: "Opening",
    status: "Sent · PostGrid",
    statusClass: "bg-success-50 text-success-600",
    bg: "linear-gradient(160deg, #101828, #475467)",
  },
  {
    title: "Quote ready",
    label: "Renewals",
    status: "Exported",
    statusClass: "bg-primary-100 text-primary-700",
    bg: "linear-gradient(160deg, #175CD3, #84CAFF)",
  },
  {
    title: "Just sold",
    label: "Elm Ave",
    status: "Draft",
    statusClass: "bg-gray-200 text-gray-800",
    bg: "linear-gradient(160deg, #854D0E, #FDE68A)",
  },
];
</script>
